import api from '@/services/apiService';

export interface CustomerListItem {
  id: number;
  name: string;
  phone: string;
  formatted_phone: string;
  email: string;
  avatar: string;
  is_app_user: boolean;
  is_new: boolean;
  total_booking: number;
  total_jam_main: number;
  total_belanja: number;
  last_booking_date: string | null;
  last_booking_date_str: string;
  last_booking_relative: string;
  first_booking_date: string | null;
  favorite_field: string;
  created_at: string;
}

export interface CustomerListPagination {
  page: number;
  limit: number;
  total_items: number;
  total_pages: number;
}

export interface CustomerListCounts {
  all: number;
  active: number;
  new: number;
}

export interface CustomerListResponse {
  customers: CustomerListItem[];
  pagination: CustomerListPagination;
  counts: CustomerListCounts;
}

export interface CustomerDetailStats {
  total_booking: number;
  upcoming_bookings_count: number;
  total_jam_main: number;
  avg_jam_main_per_booking: number;
  total_belanja: number;
  avg_belanja_per_booking: number;
  unpaid_amount: number;
  unpaid_count: number;
}

export interface CustomerDetailHabits {
  member_since: string;
  favorite_field: string;
  favorite_time: string;
  favorite_day: string;
  loyalty_status: string | null;
}

export interface WeeklyBookingBar {
  label: string;
  week_start: string;
  count: number;
  is_current: boolean;
}

export interface CustomerBookingHistoryItem {
  id: number;
  order_number: string;
  booking_date: string;
  raw_date: string;
  field_name: string;
  time_range: string;
  source: string;
  status: string;
  status_label: string;
  nominal: number;
  invoice_number?: string;
}

export interface CustomerDetailResponse {
  customer: {
    id: number;
    name: string;
    phone: string;
    formatted_phone: string;
    email: string;
    created_at: string;
    is_app_user: boolean;
  };
  stats: CustomerDetailStats;
  habits: CustomerDetailHabits;
  weekly_bookings: WeeklyBookingBar[];
  date_range_label: string;
  bookings: CustomerBookingHistoryItem[];
  booking_counts: {
    all: number;
    upcoming: number;
    past: number;
  };
  booking_pagination?: CustomerListPagination;
}

export interface UpdateCustomerPayload {
  name: string;
  phone: string;
}

class CustomerService {
  async getCustomers(
    arenaSlug: string,
    params?: {
      search?: string;
      tab?: 'all' | 'active' | 'new';
      page?: number;
      limit?: number;
    }
  ): Promise<CustomerListResponse> {
    const res = await api.get<{ data: CustomerListResponse }>(`/arenas/${arenaSlug}/customers`, {
      params,
    });
    return res.data.data;
  }

  async getCustomerDetail(
    arenaSlug: string,
    customerId: number | string,
    params?: {
      booking_page?: number;
      booking_limit?: number;
      booking_filter?: 'all' | 'upcoming' | 'past';
    }
  ): Promise<CustomerDetailResponse> {
    const res = await api.get<{ data: CustomerDetailResponse }>(
      `/arenas/${arenaSlug}/customers/${customerId}`,
      { params }
    );
    return res.data.data;
  }

  async updateCustomer(
    arenaSlug: string,
    customerId: number | string,
    payload: UpdateCustomerPayload
  ): Promise<void> {
    await api.put(`/arenas/${arenaSlug}/customers/${customerId}`, payload);
  }

  async deleteCustomer(arenaSlug: string, customerId: number | string): Promise<void> {
    await api.delete(`/arenas/${arenaSlug}/customers/${customerId}`);
  }

  async exportCustomersCsv(
    arenaSlug: string,
    params?: {
      search?: string;
      tab?: string;
    }
  ): Promise<void> {
    const res = await api.get(`/arenas/${arenaSlug}/customers/export`, {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pelanggan_${arenaSlug}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const customerService = new CustomerService();
export default customerService;
